import React, { useEffect, useRef } from 'react';
import Icona from '../Icona/Icona';
import FlatIcon from '../Icona/FlatIcon';

/*
    Mattoncini dei popup: vasca (corpo incassato), pannello informativo,
    bottoni della barra comandi, checkbox, interruttori, badge del raddoppio.
*/

//vasca: contenitore incassato; con scroll diventa la zona che scorre
export const PopWell = ({ children, scroll = false, className = "", ...rest }) => (
    <div className={"pop-well" + (scroll ? " pop-well-scroll" : "") + " " + className} {...rest}>
        {children}
    </div>
);

//pannello piatto e scuro per i contenuti da leggere
export const PopPanel = ({ children, className = "" }) => (
    <div className={"pop-panel " + className}>{children}</div>
);

//separatore inciso
export const PopDivider = () => <div className="pop-divider" aria-hidden="true"></div>;

//badge della parola per il raddoppio
export const PopX2 = ({ off = false }) => (
    <span className={"pop-x2" + (off ? " pop-x2-off" : "")}>×2</span>
);

/*
    Checkbox: state può essere true, false oppure "indt" (selezione parziale).
    Senza onClick è solo visiva (la riga che la contiene gestisce il tocco).
*/
export const PopCheck = ({ state, onClick, label, disabled = false, tone = "#0B62F5" }) => {
    const on = state === true || state === "indt";
    const icon = state === "indt" ? "minus" : state === true ? "check" : null;
    const cl = "pop-cb" + (on ? " pop-cb-on" : "") + (disabled ? " pop-cb-disabled" : "");
    const mark = icon && <FlatIcon name={icon} size={18} color={tone} weight={3.8} />;

    if (!onClick) {
        return <span className={cl} aria-hidden="true">{mark}</span>;
    }
    return (
        <button
            type="button"
            className={cl}
            aria-label={label}
            aria-pressed={state === "indt" ? "mixed" : !!state}
            disabled={disabled}
            onClick={onClick}
        >
            {mark}
        </button>
    );
}

/*
    Interruttore: checkbox ed etichetta in un bottone. Acceso è lucido nel colore dato (pop-g-*), spento è un incavo
    piatto. tone = colore della spunta
*/
export const PopToggle = ({ on, onClick, color = "blue", tone, label, disabled = false, children }) => (
    <button
        type="button"
        className={"pop-toggle " + (on ? "pop-g-" + color + " pop-gloss" : "pop-toggle-off")}
        aria-pressed={on}
        aria-label={label}
        disabled={disabled}
        onClick={onClick}
    >
        <PopCheck state={on} tone={tone} />
        <span className="pop-toggle-txt">{children}</span>
    </button>
);

/*
    Cursore: traccia incassata, riempimento lucido e manopola disegnati,
    con sopra un input range trasparente per tocco, tastiera e accessibilità.
    variant "fill" = riempimento unico (volumi), "mix" = due colori (standard / personalizzate)
    onChange scatta a ogni movimento; onCommit una sola volta al rilascio (evento "change" nativo)
*/
export const PopSlider = ({ id, value, min = 0, max = 1, step = 0.01, onChange, onCommit, variant = "fill", disabled = false, label }) => {
    const num = parseFloat(value);
    const safe = Number.isFinite(num) ? num : min;
    const p = Math.min(1, Math.max(0, (safe - min) / (max - min)));

    //React mappa onChange sull'evento "input" (continuo): il rilascio è il "change" nativo
    const inputRef = useRef(null);
    const commitRef = useRef(onCommit);
    commitRef.current = onCommit;
    useEffect(() => {
        const input = inputRef.current;
        const handleRelease = (e) => {
            if (commitRef.current) commitRef.current(e.target.value);
        };
        input.addEventListener("change", handleRelease);
        return () => input.removeEventListener("change", handleRelease);
    }, []);

    return (
        <div className={"pop-slider" + (variant === "mix" ? " pop-slider-mix" : "") + (disabled ? " pop-slider-disabled" : "")} style={{ "--p": p }}>
            <div className="pop-slider-track" aria-hidden="true">
                <span className={"pop-slider-bar pop-slider-left pop-g-" + (variant === "mix" ? "blue" : "orange")}></span>
                {variant === "mix" && <span className="pop-slider-bar pop-slider-right pop-g-orange"></span>}
            </div>
            <span className="pop-slider-knob" aria-hidden="true">
                {variant === "mix" && <><i></i><i></i></>}
            </span>
            <input
                ref={inputRef}
                id={id}
                type="range"
                className="pop-slider-input"
                min={min}
                max={max}
                step={step}
                value={safe}
                disabled={disabled}
                aria-label={label}
                onChange={(e) => onChange(e.target.value)}
            />
        </div>
    );
}

/*
    Bottone della barra comandi.
    icon = icona piatta bianca (regola generale dei pulsanti)
    gIcon = icona gamificata (solo eccezioni volute, es. "Come giocare")
*/
export const PopButton = ({ id, color = "blue", icon, iconEnd, gIcon, children, onClick, enabled = true, className = "" }) => (
    <button
        type="button"
        id={id}
        className={"pop-btn pop-g-" + (enabled ? color : "grey") + " pop-gloss " + className}
        onClick={enabled ? onClick : undefined}
        aria-disabled={enabled ? undefined : true}
    >
        {gIcon && <Icona kind={gIcon} size={44} className="pop-btn-gicon" />}
        {icon && <FlatIcon name={icon} size={24} />}
        <span className="pop-btn-txt">{children}</span>
        {iconEnd && <FlatIcon name={iconEnd} size={24} />}
    </button>
);
